import { createReducer, on } from '@ngrx/store';
import { UserAnnouncementEntitiesState } from './user-announcement-entities.state';
import { userAnnouncementEntitiesStateInitial } from './user-announcement-entities.state.initial';
import { UserAnnouncementEntitiesActions } from './user-announcement-entities.actions';

/**
 * Reducer für die Endnutzer-Announcements.
 *
 * Kein EntityAdapter — kleine Liste (max. 3), komplett geladen, navigiert per Index.
 */
export const userAnnouncementEntitiesReducers = createReducer(
  userAnnouncementEntitiesStateInitial,

  on(
    UserAnnouncementEntitiesActions.loadCurrent,
    (state): UserAnnouncementEntitiesState => ({
      ...state,
      isLoading: true,
      error: null,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.loadCurrentSuccess,
    // INFO: hasUnread wird aus unreadCount (leichtgewichtiger Count-Endpoint) oder
    // announcements.length (wenn Announcements geladen wurden) abgeleitet.
    (state, { announcements, unreadCount }): UserAnnouncementEntitiesState => ({
      ...state,
      announcements,
      hasUnread: unreadCount !== undefined ? unreadCount > 0 : announcements.length > 0,
      isLoading: false,
      isLoaded: true,
      error: null,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.loadCurrentFailure,
    (state, { error }): UserAnnouncementEntitiesState => ({
      ...state,
      isLoading: false,
      isLoaded: true,
      error,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.navigate,
    (state, { direction }): UserAnnouncementEntitiesState => {
      const currentId = state.announcements[state.currentIndex]?.id;
      const nextIndex = direction === 'next'
        ? Math.min(state.currentIndex + 1, state.announcements.length - 1)
        : Math.max(state.currentIndex - 1, 0);

      if (nextIndex === state.currentIndex) {
        return state;
      }

      const markedReadIds = currentId && !state.markedReadIds.includes(currentId)
        ? [...state.markedReadIds, currentId]
        : state.markedReadIds;
      const allRead = state.announcements.length > 0
        && state.announcements.every(a => markedReadIds.includes(a.id));

      return {
        ...state,
        currentIndex: nextIndex,
        markedReadIds,
        hasUnread: allRead ? false : state.hasUnread,
      };
    },
  ),

  // INFO: Optimistisch ID sofort in markedReadIds aufnehmen und hasUnread prüfen,
  // damit der rote Punkt sofort verschwindet (bevor der HTTP-Response eintrifft).
  on(
    UserAnnouncementEntitiesActions.markRead,
    (state, { id }): UserAnnouncementEntitiesState => {
      const markedReadIds = state.markedReadIds.includes(id)
        ? state.markedReadIds
        : [...state.markedReadIds, id];
      const allRead = state.announcements.length > 0
        && state.announcements.every(a => markedReadIds.includes(a.id));
      return {
        ...state,
        markedReadIds,
        hasUnread: allRead ? false : state.hasUnread,
      };
    },
  ),

  on(
    UserAnnouncementEntitiesActions.markReadSuccess,
    (state, { id }): UserAnnouncementEntitiesState => ({
      ...state,
      markedReadIds: state.markedReadIds.includes(id)
        ? state.markedReadIds
        : [...state.markedReadIds, id],
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.loadImageSuccess,
    (state, { imageId, blobUrl }): UserAnnouncementEntitiesState => ({
      ...state,
      imageUrls: {
        ...state.imageUrls,
        [imageId]: blobUrl,
      },
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.loadSettingsSuccess,
    (state, { enabled }): UserAnnouncementEntitiesState => ({
      ...state,
      enabled,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.updateSettings,
    (state, { enabled }): UserAnnouncementEntitiesState => ({
      ...state,
      enabled,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.updateSettingsSuccess,
    (state, { enabled }): UserAnnouncementEntitiesState => ({
      ...state,
      enabled,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.updateSettingsFailure,
    (state, { previousEnabled }): UserAnnouncementEntitiesState => ({
      ...state,
      enabled: previousEnabled,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.markAllReadSuccess,
    (state): UserAnnouncementEntitiesState => ({
      ...state,
      hasUnread: false,
    }),
  ),

  on(
    UserAnnouncementEntitiesActions.reset,
    (state): UserAnnouncementEntitiesState => {
      // INFO: Vor dem Zurücksetzen prüfen, ob alle Announcements als gelesen markiert wurden.
      const allRead = state.announcements.length > 0
        && state.announcements.every(a => state.markedReadIds.includes(a.id));
      return {
        ...userAnnouncementEntitiesStateInitial,
        enabled: state.enabled,
        hasUnread: allRead ? false : state.hasUnread,
      };
    },
  ),
);
